/// What a read looked at, in the person's words. Keyed by catalog name so a name the model
/// invents, or a capability added later, falls back to generic copy instead of being echoed.
const COPILOT_READ_SUBJECTS: Record<string, string> = {
  "attendance.approvals.list": "attendance approvals",
  "attendance.calendars.get": "a holiday calendar",
  "attendance.calendars.list": "holiday calendars",
  "attendance.day.get": "attendance for the day",
  "attendance.holidayCountries.list": "holiday countries",
  "attendance.holidayImports.preview": "the holiday import preview",
  "attendance.holidayRegions.list": "holiday regions",
  "attendance.holidays.list": "holidays",
  "attendance.records.list": "attendance records",
  "attendance.requests.list": "attendance requests",
  "attendance.setupStatus.get": "attendance setup status",
  "attendance.settings.get": "attendance settings",
  "attendance.shiftAssignments.list": "shift assignments",
  "attendance.shifts.list": "shifts",
  "attendance.today.get": "today's attendance",
  "feedback.list": "feedback",
  "goals.get": "goal details",
  "goals.list": "goals",
  "kudos.list": "kudos",
  "leave.absences.list": "absences",
  "leave.absentees.list": "who is away",
  "leave.applications.list": "leave requests",
  "leave.applications.preview": "the leave preview",
  "leave.approvals.list": "leave approvals",
  "leave.balances.list": "leave balances",
  "leave.policies.list": "leave plans",
  "leave.policyAssignments.preview": "the plan assignment preview",
  "leave.policyCoverage.get": "leave plan coverage",
  "leave.setupStatus.get": "leave setup status",
  "leave.types.list": "leave types",
  "leave.years.list": "leave years",
  "me.get": "your profile",
  "people.departments.list": "departments",
  "people.get": "a profile",
  "people.list": "people",
  "people.reports.list": "direct reports",
  "pulse.actions.get": "a pulse action",
  "pulse.actions.list": "pulse actions",
  "pulse.briefs.manager.get": "your team's pulse brief",
  "pulse.briefs.workspace.get": "the workspace pulse brief",
  "pulse.departments.list": "pulse by department",
  "pulse.insights.list": "pulse insights",
  "pulse.runs.results.get": "survey results",
  "pulse.scores.mine.get": "your pulse scores",
  "pulse.scores.team.get": "team pulse scores",
  "pulse.scores.workspace.get": "workspace pulse scores",
  "pulse.surveys.list": "pulse surveys",
  "pulse.surveys.pending.list": "pending surveys",
  "reviews.history.list": "review history",
  "reviews.tasks.list": "review tasks",
  "todos.list": "todos",
};

/// The model's own planning steps. The thinking indicator already covers them, so they get no row.
const COPILOT_PLANNING_TOOLS = new Set(["whoami", "search_capabilities", "describe_capability"]);

export type CopilotToolState = "running" | "done" | "failed";

export function isCopilotPlanningTool(toolName: string): boolean {
  return COPILOT_PLANNING_TOOLS.has(toolName);
}

function readSubject(args: unknown): string {
  if (typeof args !== "object" || args === null) return "your workspace";
  const name: unknown = Reflect.get(args, "name");
  if (typeof name !== "string") return "your workspace";
  return COPILOT_READ_SUBJECTS[name] ?? "your workspace";
}

export function copilotToolActivityLabel(args: unknown, state: CopilotToolState): string {
  const subject = readSubject(args);
  if (state === "running") return `Checking ${subject}`;
  if (state === "failed") return `Couldn't check ${subject}`;
  return `Checked ${subject}`;
}

/// A read the server refused (`{ ok: false }`) is a failure even though the call itself returned.
export function copilotToolState({
  isError,
  statusType,
  result,
}: {
  isError: boolean | undefined;
  statusType: string | undefined;
  result?: unknown;
}): CopilotToolState {
  const refused =
    typeof result === "object" && result !== null && Reflect.get(result, "ok") === false;
  if (isError || refused || statusType === "incomplete") return "failed";
  if (statusType === "running" || statusType === "requires-action") return "running";
  return "done";
}
